/**
 * Playground interceptor handlers for A/B Testing CRUD operations.
 * Matches API pattern: /v1/web/ab-test
 */
import { PlaygroundSessionStore, PlaygroundLimitError } from '../PlaygroundSessionStore.js';

const store = new PlaygroundSessionStore('pg_ab_tests', 5);
const now = () => new Date().toISOString();

/**
 * GET /v1/web/ab-test — list all A/B tests (filtered by X-Web-Game-Id header if present).
 * Response shape: the array directly (no wrapper key).
 */
export function handleGetAbTests(config, match) {
    const gameId = config.headers?.['X-Web-Game-Id'] || config.headers?.['x-web-game-id'];
    let items = store.getAll();
    if (gameId) {
        items = items.filter(item => item.gameId === gameId);
    }
    return { abTests: items };
}

/**
 * GET /v1/web/ab-test/:id — single A/B test by id.
 */
export function handleGetAbTestById(config, match) {
    const id = match[1];
    const item = store.getById(id);
    if (!item) {
        throw new Error(`A/B test with id ${id} not found`);
    }
    return item;
}

/**
 * POST /v1/web/ab-test — create new A/B test.
 */
export function handleCreateAbTest(config, match) {
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const id = PlaygroundSessionStore.getNextId();
    const newItem = {
        ...data,
        id,
        versionInfo: {
            version: 1,
            parentEntityId: `${id}-parent`,
            auditInfo: {
                lastModifiedBy: 'explorer@playground.geoplay.io',
                lastModifiedDate: now(),
            },
            versionDescription: data.changeDescription || 'Initial version',
            latest: true,
        },
    };
    return { abTest: store.create(newItem) };
}

/**
 * PUT /v1/web/ab-test/:id — update existing A/B test.
 */
export function handleUpdateAbTest(config, match) {
    const id = match[1];
    const data = typeof config.data === 'string' ? JSON.parse(config.data) : (config.data || {});
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`A/B test with id ${id} not found`);
    }
    const currentVersion = existing.versionInfo?.version || 1;
    const updated = {
        ...data,
        id,
        versionInfo: {
            ...existing.versionInfo,
            version: currentVersion + 1,
            auditInfo: {
                lastModifiedBy: 'explorer@playground.geoplay.io',
                lastModifiedDate: now(),
            },
            versionDescription: data.changeDescription || existing.versionInfo?.versionDescription || '',
            latest: true,
        },
    };
    return { abTest: store.update(id, updated) };
}

/**
 * DELETE /v1/web/ab-test/:id — remove A/B test.
 */
export function handleDeleteAbTest(config, match) {
    const id = match[1];
    store.delete(id);
    return { success: true };
}

/**
 * POST /v1/web/ab-test/:id/clone — duplicate A/B test with new id.
 */
export function handleCloneAbTest(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`A/B test with id ${id} not found`);
    }
    const newId = PlaygroundSessionStore.getNextId();
    const cloned = {
        ...existing,
        id: newId,
        name: `${existing.name} (Copy)`,
        state: 'DRAFT',
        active: 'Draft',
        versionInfo: {
            version: 1,
            parentEntityId: `${newId}-parent`,
            auditInfo: {
                lastModifiedBy: 'explorer@playground.geoplay.io',
                lastModifiedDate: now(),
            },
            versionDescription: `Cloned from ${existing.name}`,
            latest: true,
        },
    };
    return { abTest: store.create(cloned) };
}

/**
 * POST /v1/web/ab-test/:id/rollback — rollback (mock: return existing item).
 */
export function handleRollbackAbTest(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`A/B test with id ${id} not found`);
    }
    return { abTest: existing };
}

/**
 * GET /v1/web/ab-test/:id/versions — return mock version history.
 */
export function handleGetAbTestVersions(config, match) {
    const id = match[1];
    const existing = store.getById(id);
    if (!existing) {
        throw new Error(`A/B test with id ${id} not found`);
    }
    return [existing];
}
